import { ReactNode, useCallback, useState } from 'react';

import { useIntl } from 'react-intl';

import { type DocNode } from '@atlaskit/adf-schema';
import Modal, {
  ModalBody,
  ModalHeader,
  ModalTitle,
  ModalTransition,
} from '@atlaskit/modal-dialog';
import { Box, xcss } from '@atlaskit/primitives';
import {
  GicAnywhere,
  type IssueCreateEmbedSuccessPayload,
} from '@atlassian/gic-anywhere';

import { useFlag } from 'src/hooks/flag';

import { messages } from './messages';

export type CreateJiraWorkItemModalProps = {
  /** Whether the modal is currently visible. */
  isOpen: boolean;
  /** Called when the modal should be dismissed. */
  onClose: () => void;
  /**
   * Cloud id of the Jira site to seed the modal with. Resolve from
   * `useJiraCloudId()` in the caller.
   */
  cloudId: string;
  /**
   * Optional Jira project id to pre-select. Resolve from `useJiraCloudId()`
   * (it falls back to the user's `default-jira-project` preference).
   */
  projectId?: string;
  /** Pre-populated work item summary. */
  summary: string;
  /** Pre-populated work item description in ADF format. */
  descriptionAdf?: DocNode;
  /**
   * Identifier for analytics + iframe attribution. Use a stable per-feature
   * value such as `'bitbucketTestManagement'` or `'bitbucketPrComment'`.
   */
  consumer: string;
  /**
   * Override the modal title. Falls back to the generic
   * "Create Jira work item" string.
   */
  title?: ReactNode;
  /**
   * Prefix used when generating the success-flag id. Use a stable per-feature
   * value (e.g. `'create-jira-work-item-from-test'`) so multiple flags from
   * different sources don't collide.
   */
  flagIdPrefix?: string;
};

const iframeContainerStyles = xcss({
  width: '100%',
  height: '700px',
  paddingInline: 'space.050',
  paddingBottom: 'space.050',
  borderRadius: 'radius.small',
});

/**
 * Reusable modal that hosts the Atlassian "Global Issue Create Anywhere"
 * iframe to create a Jira work item.
 *
 * Designed to be feature-agnostic. Callers (e.g. test management, PR
 * comments) own the test-specific summary/description and pass them in as
 * props. Resolve `cloudId`/`projectId` with `useJiraCloudId` and gate
 * rendering with `hasSites` from the same hook.
 */
export const CreateJiraWorkItemModal = ({
  isOpen,
  onClose,
  cloudId,
  projectId,
  summary,
  descriptionAdf,
  consumer,
  title,
  flagIdPrefix = 'create-jira-work-item',
}: CreateJiraWorkItemModalProps) => {
  const { formatMessage } = useIntl();
  const { showFlag, showErrorFlag } = useFlag();
  const [hasCreatedWorkItem, setHasCreatedWorkItem] = useState(false);

  const handleSuccess = useCallback(
    (payload: IssueCreateEmbedSuccessPayload) => {
      setHasCreatedWorkItem(true);
      showFlag({
        id: `${flagIdPrefix}-success-${payload.issueKey}`,
        iconType: 'success',
        title: {
          msg: messages.successTitle,
          values: {
            workItemKey: (
              <a href={payload.link} target="_blank" rel="noopener noreferrer">
                {payload.issueKey}
              </a>
            ),
          },
        },
        autoDismiss: true,
      });
      onClose();
    },
    [flagIdPrefix, onClose, showFlag]
  );

  const handleFailure = useCallback(() => {
    showErrorFlag(messages.errorDescription, messages.errorTitle);
  }, [showErrorFlag]);

  const handleClose = useCallback(
    (isCreateSuccess?: boolean) => {
      // GIC fires onClose even after a successful create; avoid double-handling
      // because handleSuccess already closed the modal.
      if (isCreateSuccess || hasCreatedWorkItem) {
        return;
      }
      onClose();
    },
    [hasCreatedWorkItem, onClose]
  );

  return (
    <ModalTransition>
      {isOpen && (
        <Modal
          onClose={onClose}
          shouldScrollInViewport
          testId="create-jira-work-item-modal"
          width="medium"
        >
          <ModalHeader>
            <ModalTitle>
              {title ?? formatMessage(messages.modalTitle)}
            </ModalTitle>
          </ModalHeader>
          <ModalBody hasInlinePadding={false}>
            <Box xcss={iframeContainerStyles}>
              <GicAnywhere
                cloudId={cloudId}
                projectId={projectId}
                consumer={consumer}
                summary={summary}
                descriptionAdf={descriptionAdf}
                isPayloadReady
                onInit={() => {
                  /* analytics handled by GIC internally */
                }}
                onSuccess={handleSuccess}
                onFailure={handleFailure}
                onTimeout={handleFailure}
                onClose={handleClose}
              />
            </Box>
          </ModalBody>
        </Modal>
      )}
    </ModalTransition>
  );
};
